import { FormGroup } from '@angular/forms';
import {
  AssettypeViewModel,
  DeviceSettingViewModel,
} from '@components/device-setting-list/device-setting-edit-dialog/device-setting-edit-dialog.component';
import {
  AssettypeResponse,
  DeviceGroupSettingResponse,
  DeviceSettingResponse,
  DeviceSettingUpdateRequest,
} from '@entities/deviceSetting';

/** デバイス設定・設定テンプレートのセンサーの共通部分 */
export type DeviceSettingBase = {
  /** 配送機能での利用許可 */
  shipmentTracking: boolean;
  /** 位置検知 */
  locationDetected?: boolean;
  /** 位置測位技術優先度 */
  locationPriority?: string[];
  /** 落下検知イベント */
  fallDetected?: boolean;
  /** 動作検知イベント */
  mcdDetected?: boolean;
  /** 明暗検知イベント */
  lightExposureDetected?: boolean;
  /** 明暗検知イベント設定値 */
  lightExposureDetectValue?: string;
  /** 衝撃検知イベント */
  shockDetected?: boolean;
  /** 衝撃検知イベント設定値 */
  shockDetectedDetectValue?: number;
  /** 温度センサー */
  temperature?: boolean;
  /** 温度設定値 */
  temperatureSettings?: number[];
  /** 気圧センサー */
  pressure?: boolean;
  /** 気圧設定値 */
  pressureSettings?: number[];
  /** 湿度センサー */
  humidity?: boolean;
  /** 湿度設定値 */
  humiditySettings?: number[];
  /** 傾斜センサー */
  tilt?: boolean;
  /** 傾斜設定値 */
  tiltSettings?: number[];
  /** 照度センサー */
  light?: boolean;
  /** 照度設定値 */
  lightSettings?: number[];
};

/** トグルと結びついた入力フォームが存在するセンサー */
export const sensorsHavingForm: string[] = [
  'locationDetected',
  'temperature',
  'pressure',
  'humidity',
  'light',
  'tilt',
  'lightExposureDetected',
  'shockDetected',
];

/**
 * 共通のデバイス詳細設定へ変換する
 * @param deviceSettingResponse デバイス詳細設定のレスポンス内容
 * @returns デバイス詳細設定
 */
export const convertToCommonDeviceSetting = (
  deviceSettingResponse: DeviceSettingResponse | DeviceGroupSettingResponse,
) => {
  const deviceSetting = {
    samplingFrequency: deviceSettingResponse.samplingFrequency,
    reportingFrequency: deviceSettingResponse.reportingFrequency,
    shipmentTracking: deviceSettingResponse.shipmentTracking,
    locationDetected: deviceSettingResponse.locationDetected,
    locationPriority: [
      deviceSettingResponse.locationPriority_01,
      deviceSettingResponse.locationPriority_02,
      deviceSettingResponse.locationPriority_03,
    ],
    fallDetected: deviceSettingResponse.fallDetected,
    mcdDetected: deviceSettingResponse.mcdDetected,
    lightExposureDetected: deviceSettingResponse.lightExposureDetected,
    lightExposureDetectValue: deviceSettingResponse.lightExposureDetectValue,
    shockDetected: deviceSettingResponse.shockDetected,
    shockDetectedDetectValue: deviceSettingResponse.shockDetectedDetectValue,
    temperature: deviceSettingResponse.temperature,
    temperatureSettings: [
      deviceSettingResponse.temperatureAlertMax,
      deviceSettingResponse.temperaturePrealertMax,
      deviceSettingResponse.temperaturePrealertMin,
      deviceSettingResponse.temperatureAlertMin,
    ],
    pressure: deviceSettingResponse.pressure,
    pressureSettings: [
      deviceSettingResponse.pressureAlertMax,
      deviceSettingResponse.pressurePrealertMax,
      deviceSettingResponse.pressurePrealertMin,
      deviceSettingResponse.pressureAlertMin,
    ],
    humidity: deviceSettingResponse.humidity,
    humiditySettings: [
      deviceSettingResponse.humidityAlertMax,
      deviceSettingResponse.humidityPrealertMax,
      deviceSettingResponse.humidityPrealertMin,
      deviceSettingResponse.humidityAlertMin,
    ],
    tilt: deviceSettingResponse.tilt,
    tiltSettings: [
      deviceSettingResponse.tiltAlertMax,
      deviceSettingResponse.tiltPrealertMax,
      deviceSettingResponse.tiltPrealertMin,
      deviceSettingResponse.tiltAlertMin,
    ],
    light: deviceSettingResponse.light,
    lightSettings: [
      deviceSettingResponse.lightAlertMax,
      deviceSettingResponse.lightPrealertMax,
      deviceSettingResponse.lightPrealertMin,
      deviceSettingResponse.lightAlertMin,
    ],
    fmd: deviceSettingResponse.fmd,
  };

  return deviceSetting;
};

/**
 * アセットタイプViewModel一覧へ変換する
 * @param assettypeResponseList アセットタイプのレスポンス一覧
 * @returns アセットタイプ一覧
 */
export const convertToAssetTypeList = (
  assettypeResponseList: AssettypeResponse[],
): AssettypeViewModel[] => {
  const assettypeList = assettypeResponseList.map((assettype) => ({
    assettypeId: assettype['assettypeId'],
    assettypeName: assettype.assettypeName,
    tenantId: assettype.tenantId,
    deviceActiveStatus: assettype.deviceActiveStatus,
    deviceTypeId: assettype.deviceTypeId,
    samplingFrequency: assettype.samplingFrequency,
    reportingFrequency: assettype.reportingFrequency,
    shipmentTracking: assettype.shipmentTracking,
    locationDetected: assettype.locationDetected,
    locationPriority: [
      assettype.locationPriority_01,
      assettype.locationPriority_02,
      assettype.locationPriority_03,
    ],
    fallDetected: assettype.fallDetected,
    mcdDetected: assettype.mcdDetected,
    lightExposureDetected: assettype.lightExposureDetected,
    lightExposureDetectValue: assettype.lightExposureDetectValue,
    shockDetected: assettype.shockDetected,
    shockDetectedDetectValue: assettype.shockDetectedDetectValue,
    sensorDescription: assettype.sensorDescription,
    temperature: assettype.temperature,
    temperatureSettings: [
      assettype.temperatureAlertMax,
      assettype.temperaturePrealertMax,
      assettype.temperaturePrealertMin,
      assettype.temperatureAlertMin,
    ],
    pressure: assettype.pressure,
    pressureSettings: [
      assettype.pressureAlertMax,
      assettype.pressurePrealertMax,
      assettype.pressurePrealertMin,
      assettype.pressureAlertMin,
    ],
    humidity: assettype.humidity,
    humiditySettings: [
      assettype.humidityAlertMax,
      assettype.humidityPrealertMax,
      assettype.humidityPrealertMin,
      assettype.humidityAlertMin,
    ],
    tilt: assettype.tilt,
    tiltSettings: [
      assettype.tiltAlertMax,
      assettype.tiltPrealertMax,
      assettype.tiltPrealertMin,
      assettype.tiltAlertMin,
    ],
    light: assettype.light,
    lightSettings: [
      assettype.lightAlertMax,
      assettype.lightPrealertMax,
      assettype.lightPrealertMin,
      assettype.lightAlertMin,
    ],
    fmd: assettype.fmd,
    createdBy: assettype.createdBy,
    updatedBy: assettype.updatedBy,
    createdAt: assettype.createdAt,
    updatedAt: assettype.updatedAt,
  }));
  return assettypeList;
};

/**
 * デバイス詳細設定のリスエストに変換する
 * @param editedDeviceSetting 編集されたデバイス
 * @returns
 */
export const convertToDeviceSettingRequest = (
  editedDeviceSetting: DeviceSettingViewModel,
): DeviceSettingUpdateRequest => {
  const deviceSettingRequest: DeviceSettingUpdateRequest = {
    shipmentTracking: editedDeviceSetting.shipmentTracking,
  };
  const samplingFrequency = editedDeviceSetting.samplingFrequency;
  if (samplingFrequency != null) {
    deviceSettingRequest.samplingFrequency = samplingFrequency;
  }
  const reportingFrequency = editedDeviceSetting.reportingFrequency;
  if (reportingFrequency != null) {
    deviceSettingRequest.reportingFrequency = reportingFrequency;
  }

  const locationDetected = editedDeviceSetting.locationDetected;
  if (locationDetected != null) {
    deviceSettingRequest.locationDetected = locationDetected;
    const [locationPriority01, locationPriority02, locationPriority03] =
      editedDeviceSetting.locationPriority || [];
    deviceSettingRequest.locationPriority_01 = locationPriority01 || '';
    deviceSettingRequest.locationPriority_02 = locationPriority02 || '';
    deviceSettingRequest.locationPriority_03 = locationPriority03 || '';
  }
  const fallDetected = editedDeviceSetting.fallDetected;
  if (fallDetected != null) {
    deviceSettingRequest.fallDetected = fallDetected;
  }
  const mcdDetected = editedDeviceSetting.mcdDetected;
  if (mcdDetected != null) {
    deviceSettingRequest.mcdDetected = mcdDetected;
  }
  const lightExposureDetected = editedDeviceSetting.lightExposureDetected;
  if (lightExposureDetected != null) {
    deviceSettingRequest.lightExposureDetected = lightExposureDetected;
    deviceSettingRequest.lightExposureDetectValue =
      editedDeviceSetting.lightExposureDetectValue;
  }
  const shockDetected = editedDeviceSetting.shockDetected;
  if (shockDetected != null) {
    deviceSettingRequest.shockDetected = shockDetected;
    deviceSettingRequest.shockDetectedDetectValue =
      editedDeviceSetting.shockDetectedDetectValue;
  }
  const temperature = editedDeviceSetting.temperature;
  if (temperature != null) {
    deviceSettingRequest.temperature = temperature;
    const [alertMax, alertMin, prealertMax, prealertMin] =
      editedDeviceSetting.temperatureSettings || [];
    deviceSettingRequest.temperatureAlertMax = alertMax || 0;
    deviceSettingRequest.temperatureAlertMin = alertMin || 0;
    deviceSettingRequest.temperaturePrealertMax = prealertMax || 0;
    deviceSettingRequest.temperaturePrealertMin = prealertMin || 0;
  }
  const pressure = editedDeviceSetting.pressure;
  if (pressure != null) {
    deviceSettingRequest.pressure = pressure;
    const [alertMax, alertMin, prealertMax, prealertMin] =
      editedDeviceSetting.pressureSettings || [];
    deviceSettingRequest.pressureAlertMax = alertMax || 0;
    deviceSettingRequest.pressureAlertMin = alertMin || 0;
    deviceSettingRequest.pressurePrealertMax = prealertMax || 0;
    deviceSettingRequest.pressurePrealertMin = prealertMin || 0;
  }
  const humidity = editedDeviceSetting.humidity;
  if (humidity != null) {
    deviceSettingRequest.humidity = humidity;
    const [alertMax, alertMin, prealertMax, prealertMin] =
      editedDeviceSetting.humiditySettings || [];
    deviceSettingRequest.humidityAlertMax = alertMax || 0;
    deviceSettingRequest.humidityAlertMin = alertMin || 0;
    deviceSettingRequest.humidityPrealertMax = prealertMax || 0;
    deviceSettingRequest.humidityPrealertMin = prealertMin || 0;
  }
  const tilt = editedDeviceSetting.tilt;
  if (tilt != null) {
    deviceSettingRequest.tilt = tilt;
    const [alertMax] = editedDeviceSetting.tiltSettings || [];
    deviceSettingRequest.tiltAlertMax = alertMax || 0;
  }
  const light = editedDeviceSetting.light;
  if (light != null) {
    deviceSettingRequest.light = light;
    const [alertMax, alertMin, prealertMax, prealertMin] =
      editedDeviceSetting.lightSettings || [];
    deviceSettingRequest.lightAlertMax = alertMax || 0;
    deviceSettingRequest.lightAlertMin = alertMin || 0;
    deviceSettingRequest.lightPrealertMax = prealertMax || 0;
    deviceSettingRequest.lightPrealertMin = prealertMin || 0;
  }
  const fmd = editedDeviceSetting.fmd;
  if (fmd != null) {
    deviceSettingRequest.fmd = fmd;
  }

  return deviceSettingRequest;
};

/**
 * 各センサーの種類に応じて、各フォームを初期値にリセットする。
 * @param フォームグループ
 * @param 編集ダイアログのデバイス詳細/設定テンプレートの初期値
 * @param 対象センサー
 */
export function resetSensorSettings(
  formGroup: FormGroup,
  initAssetType: DeviceSettingBase,
  sensor: string,
): void {
  const setControlValue = (
    controlName: string,
    value: string | number | undefined,
  ) => {
    const ctrl = formGroup.get(controlName);
    if (!ctrl) {
      return;
    }
    ctrl.setValue(value);
  };

  switch (sensor) {
    case 'locationDetected': {
      const vals = initAssetType['locationPriority'] as string[] | undefined;
      const settingsGroup = formGroup.get(
        'locationPriority',
      ) as FormGroup | null;
      if (!settingsGroup) {
        return;
      }
      if (!Array.isArray(vals)) {
        // 初期値がない場合はクリア（undefined）する
        settingsGroup.patchValue({
          locationPriority1: undefined,
          locationPriority2: undefined,
          locationPriority3: undefined,
        });
      } else {
        settingsGroup.patchValue({
          locationPriority1: vals[0],
          locationPriority2: vals[1],
          locationPriority3: vals[2],
        });
      }
      break;
    }

    case 'temperature':
    case 'pressure':
    case 'humidity':
    case 'light': {
      // 各センサー共通の4つのサフィックス
      const suffixes = ['AlertMax', 'PrealertMax', 'PrealertMin', 'AlertMin'];
      const settingsKey = `${sensor}Settings` as keyof DeviceSettingBase;
      const vals = initAssetType[settingsKey] as number[] | undefined;

      // 入力フォーム群の FormGroup があるか確認し、存在すれば初期値を設定。
      const settingsGroup = formGroup.get(
        `${sensor}Settings`,
      ) as FormGroup | null;
      if (Array.isArray(vals) && vals.length >= 4) {
        const patch: { [k: string]: number } = {};
        suffixes.forEach((suf, idx) => {
          const controlName = `${sensor}${suf}`; // e.g. temperatureAlertMax
          patch[controlName] = Number(vals[idx]);
        });
        if (!settingsGroup) {
          return;
        }
        settingsGroup.patchValue(patch);
      } else {
        // 初期値が配列でない、または長さ不足の場合、各コントロールを undefined にする
        suffixes.forEach((suf) => {
          const controlName = `${sensor}${suf}`;
          const value = undefined;
          setControlValue(controlName, value);
        });
      }
      break;
    }

    case 'tilt': {
      // 初期値は 単一であるが number[] で保存されているため、その形式に合わせて処理。
      const vals = initAssetType['tiltSettings'] as number[] | undefined;
      const value = Array.isArray(vals) ? Number(vals[0]) : undefined;
      const settingsGroup = formGroup.get('tiltSettings') as FormGroup | null;

      if (!settingsGroup) {
        return;
      }
      settingsGroup.patchValue({ tiltAlertMax: value });
      break;
    }

    case 'lightExposureDetected': {
      const value = initAssetType['lightExposureDetectValue'];
      setControlValue('lightExposureDetectValue', value);
      break;
    }

    case 'shockDetected': {
      const value = initAssetType['shockDetectedDetectValue'];
      setControlValue('shockDetectedDetectValue', value);
      break;
    }

    default:
      break;
  }
}
